import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { Product, Order } from '../types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

let supabase: SupabaseClient | null = null;

if (supabaseUrl && supabaseAnonKey && supabaseUrl !== 'your_supabase_url_here') {
  try {
    supabase = createClient(supabaseUrl, supabaseAnonKey);
  } catch (e) {
    console.error('Failed to initialize Supabase client:', e);
  }
} else {
  console.warn('Supabase URL or Anon Key is missing or default. The app is running in a limited mode. Please check your .env file.');
}

export { supabase };

export const fetchProducts = async (): Promise<Product[]> => {
  if (!supabase) {
    console.warn('Supabase not configured, returning empty products list.');
    return [];
  }

  const { data, error } = await supabase
    .from('products')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching products:', error);
    return [];
  }

  return data as Product[];
};

export const updateProduct = async (product: Product): Promise<void> => {
  if (!supabase) throw new Error('Supabase not configured');

  const { error } = await supabase
    .from('products')
    .update({
      name: product.name,
      description: product.description,
      price: product.price,
      image: product.image,
      category: product.category,
      stock: product.stock,
      sizes: product.sizes || [],
    })
    .eq('id', product.id);

  if (error) {
    console.error('Error updating product:', error);
    throw error;
  }
};

export const createProduct = async (product: Omit<Product, 'id'>): Promise<Product> => {
  if (!supabase) throw new Error('Supabase not configured');

  const { data, error } = await supabase
    .from('products')
    .insert([{ ...product, sizes: product.sizes || [] }])
    .select()
    .single();

  if (error) {
    console.error('Error creating product:', error);
    throw error;
  }

  return data as Product;
};

export const deleteProduct = async (id: string): Promise<void> => {
  if (!supabase) throw new Error('Supabase not configured');

  const { error } = await supabase
    .from('products')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};

export const submitOrder = async (order: Order): Promise<Order> => {
  if (!supabase) throw new Error('Supabase not configured');

  // 1. Check stock availability first (Optimistic Check)
  for (const item of order.items) {
    const { data: product, error } = await supabase
      .from('products')
      .select('stock, name')
      .eq('id', item.id)
      .single();

    if (error || !product) {
      throw new Error(`Product ${item.name} not found`);
    }

    if (product.stock < item.quantity) {
      throw new Error(`Insufficient stock for ${item.name}. Only ${product.stock} available.`);
    }
  }

  // 2. Insert Order
  const dbOrder = {
    customer_name: order.customerName,
    email: order.email,
    phone: order.phone,
    address: order.address,
    items: order.items,
    total: order.total,
    payment_method: order.paymentMethod,
    payment_status: order.status,
    paystack_reference: (order as any).paystackReference || null as string | null,
    created_at: new Date().toISOString()
  };

  const { data, error: orderError } = await supabase
    .from('orders')
    .insert([dbOrder])
    .select()
    .single();

  if (orderError) {
    console.error('Error submitting order:', orderError);
    throw orderError;
  }

  // 3. Update Stock
  for (const item of order.items) {
    const { error: stockError } = await supabase.rpc('decrement_stock', {
      product_id: item.id,
      quantity: item.quantity
    });

    // Fallback if RPC doesn't exist (client-side update - less safe but works for now)
    if (stockError) {
      // Fetch current again to be safe
      const { data: current } = await supabase.from('products').select('stock').eq('id', item.id).single();
      if (current) {
        await supabase.from('products').update({ stock: current.stock - item.quantity }).eq('id', item.id);
      }
    }
  }

  // 4. Send SMS Notification (Arkesel)
  // Use Paystack reference as the Short ID if available, otherwise fallback to UUID substr
  const shortId = (order as any).paystackReference || data.id.substring(0, 6).toUpperCase();

  const smsMessage = `Hello ${order.customerName}, your wardrobe just got a level-up! 🧥 Your order #${shortId} is officially in the works. We’ll text you shortly about delivery.`;
  // Dynamic import to avoid circular dependencies if any, though likely not needed here
  const { sendSMS } = await import('./arkeselService');
  await sendSMS(order.phone, smsMessage);

  return {
    ...order,
    id: data.id,
    shortId: shortId
  };
};

export const fetchUniqueProductImages = async (): Promise<string[]> => {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from('products')
    .select('image')
    .not('image', 'is', null);

  if (error) {
    console.error('Error fetching product images:', error);
    return [];
  }

  // Filter out duplicates and empty strings
  const images = data
    .map(p => p.image)
    .filter((img): img is string => !!img && img.trim().length > 0);

  return Array.from(new Set(images));
};

export const uploadProductImage = async (file: File): Promise<string | null> => {
  if (!supabase) {
    console.warn('Supabase not configured for uploads');
    return null;
  }

  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('product-images')
      .upload(filePath, file);

    if (uploadError) {
      throw uploadError;
    }

    const { data } = supabase.storage
      .from('product-images')
      .getPublicUrl(filePath);

    return data.publicUrl;
  } catch (error) {
    console.error('Error uploading image:', error);
    return null;
  }
};